import { CheckCircle2, ChevronRight, Clock, TrendingUp, XCircle } from "lucide-react";
import React, { useState } from "react";

import Spinner from "@/app/components/Spinner";
import { Button } from "@/app/components/ui/button";
import { FitCheckAssessment } from "@/app/Views/FitCheck/config/api";
import FitCheck from "@/app/Views/FitCheck/FitCheck";

import { JobDetails } from "../utils/helpers";

type MatchLevel = "high" | "partial" | "low" | "none";
type FitCheckState = "idle" | "loading" | "active" | "results";
type Language = "en" | "sw";

/**
 * Bilingual micro-copy for FitCheck section
 */
const fitCheckCopy = {
	idle: {
		header: {
			en: "Before you apply — check your fit",
			sw: "Kabla hujaomba — angalia kama unalingana na kazi hii",
		},
		body: {
			en: "See how your experience compares to this role. We'll highlight strengths, gaps, and your next best step.",
			sw: "Tutaangalia uzoefu wako dhidi ya mahitaji ya kazi hii, na kukuonyesha hatua inayofuata.",
		},
		cta: {
			en: "Check my fit for this role",
			sw: "Kagua ulinganifu wangu na kazi hii",
		},
		helper: {
			en: "Takes 2–3 minutes. No application submitted.",
			sw: "Dakika 2–3. Hakuna maombi yatakayowasilishwa.",
		},
	},
	loading: {
		header: {
			en: "Checking your fit...",
			sw: "Tunachambua ulinganifu wako...",
		},
		body: {
			en: "Looking for your profile and any previous assessments.",
			sw: "Tunaangalia wasifu wako na tathmini zozote zilizopita.",
		},
	},
	active: {
		header: {
			en: "Check your fit",
			sw: "Kagua ulinganifu wako",
		},
		body: {
			en: "Select your resume to analyze your fit for this role",
			sw: "Chagua CV yako kuchambua ulinganifu wako na nafasi hii",
		},
		cancel: {
			en: "Cancel",
			sw: "Ghairi",
		},
	},
	notLoggedIn: {
		header: {
			en: "Check your fit for this role",
			sw: "Kagua ulinganifu wako na nafasi hii",
		},
		body: {
			en: "Sign in to see how your experience compares to this role's requirements.",
			sw: "Ingia ili kuona jinsi uzoefu wako unavyolinganishwa na mahitaji ya nafasi hii.",
		},
		cta: {
			en: "Sign in to check fit",
			sw: "Ingia kuchunguza ulinganifu",
		},
	},
	highMatch: {
		header: {
			en: "Strong match",
			sw: "Ulinganifu mzuri sana",
		},
		badge: {
			en: "Ready to apply",
			sw: "Tayari kuomba",
		},
		body: {
			en: "Your experience aligns well with this role. You're well-positioned to apply with confidence.",
			sw: "Uzoefu wako unaendana vyema na kazi hii. Una nafasi nzuri ya kuomba kwa ujasiri.",
		},
		strengths: {
			en: "Your key strengths",
			sw: "Nguvu zako kuu",
		},
		applyCta: {
			en: "Apply for this job",
			sw: "Omba kazi hii",
		},
		viewCta: {
			en: "View detailed results",
			sw: "Angalia matokeo kwa undani",
		},
	},
	partialMatch: {
		header: {
			en: "Potential match",
			sw: "Unafanana kwa kiasi",
		},
		body: {
			en: "You meet several key requirements. A few gaps are worth reviewing before applying.",
			sw: "Unatimiza baadhi ya mahitaji muhimu. Kuna maeneo machache ya kuzingatia kabla ya kuomba.",
		},
		gaps: {
			en: "Areas to consider",
			sw: "Maeneo ya kuzingatia",
		},
		cta: {
			en: "View my FitCheck results",
			sw: "Angalia matokeo ya FitCheck yangu",
		},
	},
	lowMatch: {
		header: {
			en: "Not the best match — yet",
			sw: "Siyo ulinganifu bora kwa sasa",
		},
		body: {
			en: "This role may be challenging based on your current profile. We've highlighted areas to improve or similar roles to explore.",
			sw: "Kwa uzoefu wa sasa, kazi hii inaweza kuwa changamoto. Tumekuonyesha maeneo ya kuboresha au kazi mbadala zinazofaa zaidi.",
		},
		focusAreas: {
			en: "Areas to develop",
			sw: "Maeneo ya kuendeleza",
		},
		cta: {
			en: "View recommendations",
			sw: "Angalia mapendekezo",
		},
	},
};

/**
 * Language toggle component
 */
function LanguageToggle({ lang, onToggle }: { lang: Language; onToggle: (lang: Language) => void }) {
	return (
		<button
			type="button"
			role="switch"
			aria-checked={lang === "sw"}
			onClick={() => onToggle(lang === "en" ? "sw" : "en")}
			className="w-12 h-6 bg-purple-600 rounded-full border border-purple-200 flex items-center p-1 relative focus:outline-hidden shadow-xs"
			aria-label="Toggle language"
		>
			{/* labels inside track */}
			<div className="absolute inset-0 flex items-center justify-between px-2 pointer-events-none">
				<span className={`text-[8px] font-medium ${lang === "sw" ? "text-white" : "opacity-0"}`}>SW</span>
				<span className={`text-[8px] font-medium ${lang === "en" ? "text-white" : "opacity-0"}`}>EN</span>
			</div>

			{/* small knob */}
			<div
				className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transform transition-all duration-200 ${
					lang === "sw" ? "right-1" : "left-1"
				}`}
				style={{ pointerEvents: "none" }}
			/>
		</button>
	);
}

interface FitCheckSectionProps {
	isLoggedIn: boolean;
	jobDetails: JobDetails;
	fitCheckResult?: FitCheckAssessment | null;
	hasFitCheckResult: boolean;
	onFitCheckClick: () => void;
	onApplyClick?: () => void;
	lang?: Language;
	hideIdleCTA?: boolean;
}

/**
 * Determine match level based on FitCheck assessment
 */
function getMatchLevel(assessment: FitCheckAssessment): MatchLevel {
	const { baselineCheck } = assessment;
	const statuses = [
		baselineCheck.education.status,
		baselineCheck.experience.status,
		baselineCheck.skills.status,
		baselineCheck.certification.status,
	];

	const meetCount = statuses.filter((s) => s === "MEET").length;
	const missingCount = statuses.filter((s) => s === "MISSING").length;

	if (meetCount >= 3) return "high";
	if (missingCount >= 3) return "low";
	return "partial";
}

export function FitCheckSection({
	isLoggedIn,
	jobDetails,
	fitCheckResult,
	hasFitCheckResult,
	onFitCheckClick,
	onApplyClick,
	lang: externalLang,
	hideIdleCTA = false,
}: FitCheckSectionProps) {
	const [fitCheckState, setFitCheckState] = useState<FitCheckState>("idle");
	const [internalLang, setInternalLang] = useState<Language>("en");

	// Use external lang if provided, otherwise use internal state
	const lang = externalLang ?? internalLang;
	const setLang = externalLang ? () => {} : setInternalLang;

	// Expose a method to trigger FitCheck from outside (via ref or global event)
	// Listen for custom event to start FitCheck
	React.useEffect(() => {
		const handleStartFitCheck = () => {
			if (isLoggedIn) {
				setFitCheckState("loading");
				setTimeout(() => {
					setFitCheckState("active");
				}, 500);
			}
		};

		window.addEventListener("startFitCheck", handleStartFitCheck);
		return () => window.removeEventListener("startFitCheck", handleStartFitCheck);
	}, [isLoggedIn]);

	// Handle FitCheck button click - show inline FitCheck
	const handleStartFitCheck = () => {
		if (!isLoggedIn) {
			// Redirect to login
			onFitCheckClick();
			return;
		}
		setFitCheckState("loading");
		// Short delay to show loading, then show FitCheck
		setTimeout(() => {
			setFitCheckState("active");
		}, 500);
	};

	// Not logged in state
	if (!isLoggedIn) {
		return (
			<div className="bg-gray-50 rounded-xl p-6 border border-gray-200 mb-6">
				<div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
					<img src="/images/filling-survey.png" className="w-24 sm:w-32" alt="Check your fit" />
					<div className="flex-1">
						<div className="flex items-center justify-between mb-1">
							<h3 className="text-lg font-semibold text-gray-900">{fitCheckCopy.notLoggedIn.header[lang]}</h3>
							{!externalLang && <LanguageToggle lang={lang} onToggle={setLang} />}
						</div>
						<p className="text-sm text-gray-600">{fitCheckCopy.notLoggedIn.body[lang]}</p>
					</div>
					<Button onClick={onFitCheckClick} variant="outline">
						{fitCheckCopy.notLoggedIn.cta[lang]}
					</Button>
				</div>
			</div>
		);
	}

	// Loading state - checking for existing FitCheck
	if (fitCheckState === "loading") {
		return (
			<div className="bg-linear-to-r from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-100 mb-6">
				<div className="flex flex-col items-center justify-center py-8 gap-4">
					<div className="w-8 h-8">
						<Spinner />
					</div>
					<div className="text-center">
						<h3 className="text-lg font-semibold text-gray-900 mb-1">{fitCheckCopy.loading.header[lang]}</h3>
						<p className="text-sm text-gray-600">{fitCheckCopy.loading.body[lang]}</p>
					</div>
				</div>
			</div>
		);
	}

	// Active FitCheck - show the FitCheck component inline
	if (fitCheckState === "active") {
		return (
			<div id="fitcheck-section" className="bg-white rounded-xl border border-gray-200 mb-6 overflow-hidden">
				<div className="bg-linear-to-r from-purple-50 to-indigo-50 px-6 py-4 border-b border-purple-100">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-3">
							<img src="/images/filling-survey.png" className="w-10 h-10 object-contain" alt="" />
							<div>
								<h3 className="text-lg font-semibold text-gray-900">{fitCheckCopy.active.header[lang]}</h3>
								<p className="text-xs text-gray-500">{fitCheckCopy.active.body[lang]}</p>
							</div>
						</div>
						<div className="flex items-center gap-3">
							{!externalLang && <LanguageToggle lang={lang} onToggle={setLang} />}
							<Button variant="ghost" size="sm" onClick={() => setFitCheckState("idle")}>
								{fitCheckCopy.active.cancel[lang]}
							</Button>
						</div>
					</div>
				</div>
				<div className="p-4 min-h-[200px]">
					<FitCheck jobDetails={jobDetails as unknown as Parameters<typeof FitCheck>[0]["jobDetails"]} />
				</div>
			</div>
		);
	}

	// Has FitCheck result - show based on match level
	if (hasFitCheckResult && fitCheckResult) {
		const matchLevel = getMatchLevel(fitCheckResult);

		if (matchLevel === "high") {
			return (
				<HighMatchState
					fitCheck={fitCheckResult}
					onApplyClick={onApplyClick}
					onViewAgain={handleStartFitCheck}
					lang={lang}
					onLangToggle={!externalLang ? setLang : undefined}
				/>
			);
		}

		if (matchLevel === "partial") {
			return (
				<PartialMatchState
					fitCheck={fitCheckResult}
					onViewAgain={handleStartFitCheck}
					lang={lang}
					onLangToggle={!externalLang ? setLang : undefined}
				/>
			);
		}

		return (
			<LowMatchState
				fitCheck={fitCheckResult}
				onViewAgain={handleStartFitCheck}
				lang={lang}
				onLangToggle={!externalLang ? setLang : undefined}
			/>
		);
	}

	// Idle state - No FitCheck result yet
	// If hideIdleCTA is true, don't render the idle CTA (it's shown elsewhere)
	if (hideIdleCTA) {
		return null;
	}

	// Show CTA with image
	return (
		<div
			id="fitcheck-section"
			className="bg-linear-to-r from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-100 mb-6"
		>
			<div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
				<img src="/images/filling-survey.png" className="w-24 sm:w-32" alt="Check your fit" />
				<div className="flex-1">
					<div className="flex items-center justify-between mb-1">
						<h3 className="text-lg font-semibold text-gray-900">{fitCheckCopy.idle.header[lang]}</h3>
						{!externalLang && <LanguageToggle lang={lang} onToggle={setLang} />}
					</div>
					<p className="text-sm text-gray-600 mb-2">{fitCheckCopy.idle.body[lang]}</p>
					<p className="text-xs text-gray-500 flex items-center gap-1">
						<Clock className="h-3 w-3" />
						{fitCheckCopy.idle.helper[lang]}
					</p>
				</div>
				<Button onClick={handleStartFitCheck} size="sm" variant="outlineSecondary">
					{fitCheckCopy.idle.cta[lang]}
				</Button>
			</div>
		</div>
	);
}

// High Match State Component
interface MatchStateProps {
	fitCheck: FitCheckAssessment;
	onApplyClick?: () => void;
	onViewAgain?: () => void;
	lang: Language;
	onLangToggle?: (lang: Language) => void;
}

function HighMatchState({ fitCheck, onApplyClick, onViewAgain, lang, onLangToggle }: MatchStateProps) {
	return (
		<div className="bg-linear-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200 mb-6">
			<div className="flex flex-col sm:flex-row items-start gap-4">
				<div className="p-3 bg-green-100 rounded-lg">
					<CheckCircle2 className="h-6 w-6 text-green-600" />
				</div>
				<div className="flex-1">
					<div className="flex items-center justify-between mb-1">
						<div className="flex items-center gap-2">
							<h3 className="text-lg font-semibold text-green-800">{fitCheckCopy.highMatch.header[lang]}</h3>
							<span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
								{fitCheckCopy.highMatch.badge[lang]}
							</span>
						</div>
						{onLangToggle && <LanguageToggle lang={lang} onToggle={onLangToggle} />}
					</div>
					<p className="text-sm text-gray-700 mb-3">{fitCheckCopy.highMatch.body[lang]}</p>

					{/* Key strengths */}
					{fitCheck.strengths.length > 0 && (
						<div className="mb-4">
							<p className="text-xs font-medium text-gray-500 uppercase mb-2">
								{fitCheckCopy.highMatch.strengths[lang]}
							</p>
							<div className="flex flex-wrap gap-2">
								{fitCheck.strengths.slice(0, 3).map((strength, idx) => (
									<span key={idx} className="px-2 py-1 bg-white text-gray-700 text-xs rounded border border-green-200">
										{strength}
									</span>
								))}
							</div>
						</div>
					)}

					<div className="flex flex-wrap gap-3">
						{onApplyClick && (
							<Button onClick={onApplyClick} className="bg-green-600 hover:bg-green-700 text-white">
								{fitCheckCopy.highMatch.applyCta[lang]}
								<ChevronRight className="h-4 w-4 ml-1" />
							</Button>
						)}
						{onViewAgain && (
							<Button onClick={onViewAgain} variant="outline" size="sm">
								{fitCheckCopy.highMatch.viewCta[lang]}
							</Button>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

// Partial Match State Component
function PartialMatchState({ fitCheck, onViewAgain, lang, onLangToggle }: Omit<MatchStateProps, "onApplyClick">) {
	return (
		<div className="bg-linear-to-r from-amber-50 to-yellow-50 rounded-xl p-6 border border-amber-200 mb-6">
			<div className="flex flex-col sm:flex-row items-start gap-4">
				<div className="p-3 bg-amber-100 rounded-lg">
					<TrendingUp className="h-6 w-6 text-amber-600" />
				</div>
				<div className="flex-1">
					<div className="flex items-center justify-between mb-1">
						<h3 className="text-lg font-semibold text-amber-800">{fitCheckCopy.partialMatch.header[lang]}</h3>
						{onLangToggle && <LanguageToggle lang={lang} onToggle={onLangToggle} />}
					</div>
					<p className="text-sm text-gray-700 mb-3">{fitCheckCopy.partialMatch.body[lang]}</p>

					{/* Show gaps if any */}
					{fitCheck.gaps.length > 0 && (
						<div className="mb-4">
							<p className="text-xs font-medium text-gray-500 uppercase mb-2">{fitCheckCopy.partialMatch.gaps[lang]}</p>
							<div className="flex flex-wrap gap-2">
								{fitCheck.gaps.slice(0, 2).map((gap, idx) => (
									<span key={idx} className="px-2 py-1 bg-white text-gray-700 text-xs rounded border border-amber-200">
										{gap}
									</span>
								))}
							</div>
						</div>
					)}

					<div className="flex flex-wrap gap-3">
						{onViewAgain && (
							<Button onClick={onViewAgain} className="bg-amber-600 hover:bg-amber-700 text-white">
								{fitCheckCopy.partialMatch.cta[lang]}
								<ChevronRight className="h-4 w-4 ml-1" />
							</Button>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

// Low Match State Component
function LowMatchState({ fitCheck, onViewAgain, lang, onLangToggle }: Omit<MatchStateProps, "onApplyClick">) {
	return (
		<div className="bg-linear-to-r from-gray-50 to-slate-50 rounded-xl p-6 border border-gray-200 mb-6">
			<div className="flex flex-col sm:flex-row items-start gap-4">
				<div className="p-3 bg-gray-100 rounded-lg">
					<XCircle className="h-6 w-6 text-gray-500" />
				</div>
				<div className="flex-1">
					<div className="flex items-center justify-between mb-1">
						<h3 className="text-lg font-semibold text-gray-800">{fitCheckCopy.lowMatch.header[lang]}</h3>
						{onLangToggle && <LanguageToggle lang={lang} onToggle={onLangToggle} />}
					</div>
					<p className="text-sm text-gray-700 mb-3">{fitCheckCopy.lowMatch.body[lang]}</p>

					{/* Focus areas */}
					{fitCheck.focusAreas && fitCheck.focusAreas.length > 0 && (
						<div className="mb-4">
							<p className="text-xs font-medium text-gray-500 uppercase mb-2">
								{fitCheckCopy.lowMatch.focusAreas[lang]}
							</p>
							<div className="flex flex-wrap gap-2">
								{fitCheck.focusAreas.slice(0, 3).map((area, idx) => (
									<span key={idx} className="px-2 py-1 bg-white text-gray-700 text-xs rounded border border-gray-200">
										{area}
									</span>
								))}
							</div>
						</div>
					)}

					<div className="flex flex-wrap gap-3">
						{onViewAgain && (
							<Button onClick={onViewAgain} variant="outline">
								{fitCheckCopy.lowMatch.cta[lang]}
								<ChevronRight className="h-4 w-4 ml-1" />
							</Button>
						)}
					</div>

					{/* Encouragement note */}
					{fitCheck.encouragementNote && (
						<p className="mt-4 text-xs text-gray-500 italic border-t border-gray-200 pt-3">
							{fitCheck.encouragementNote}
						</p>
					)}
				</div>
			</div>
		</div>
	);
}
